/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Capacitor } from "@capacitor/core";
import { colors } from "@farmlands/mui-theme";
import { Alert, Stack } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import Input from "../../../../components/Input";
import MaterialSymbol from "../../../../components/MaterialSymbol";
import Typography from "../../../../components/Typography";
import { UNIT_MAP_LONG, UNIT_MAP_PLURAL } from "../../../../util/consts";
import { formatNumber, formatPrice } from "../../../../util/helpers";
import AdditivesOption, {
  FeedRateDisplay,
  feedRates,
} from "../../../sales-agreements/components/CampaignModal/AdditivesOption";

import { ProductRequest } from "./types";

export interface ProductLineValues {
  requestQuantity: string;
  feedRate?: string;
  additives?: string;
}

interface ProductLineProps {
  product: ProductRequest;
  amounts: Record<string, ProductLineValues>;
  showAdditives?: boolean;
  handleAmountChange: (product: ProductRequest, updatedValues: Partial<ProductLineValues>) => void;
  disabled?: boolean;
  month?: string;
  hideRemainder?: boolean;
}

const ProductLine: React.FC<ProductLineProps> = ({
  product,
  amounts,
  handleAmountChange,
  showAdditives,
  disabled,
  month,
  hideRemainder = false,
}) => {
  const committedAmount = product.quantity.committed;
  const orderedAmount =
    product.quantity.released + product.quantity.delivered + product.quantity.invoiced;
  const requestQuantity = Number(amounts[product.productVariantNumber]?.requestQuantity ?? "0");

  const totalAmount = requestQuantity * product.unitPrice;

  const [errorMessage, setErrorMessage] = useState<ErrorBoxProps | undefined>();
  const toleranceMaxed = product.maxWithTolerance - product.maxLeft >= product.maxWithTolerance;
  const maxLeftFormatted = formatNumber(product.maxLeft > 0 ? product.maxLeft : 0);

  const validateAmount = (value: string) => {
    const requestedAmount = formatNumber(Number(value) || 0);
    if (requestedAmount > formatNumber(product.maxWithTolerance)) {
      setErrorMessage({
        title: "You requested amount will exceed the total on your agreement.",
        description: [
          "Please contact your Farmlands representative if you'd like to order more or to create a new agreement.",
          <br key="br1" />,
          <br key="br2" />,
          `You have ${maxLeftFormatted} ${UNIT_MAP_PLURAL[product.unit as keyof typeof UNIT_MAP_PLURAL]?.toLowerCase()} remaining.`,
        ],
        color: colors.error100,
      });
    } else if (
      requestedAmount > formatNumber(product.quantity.remaining) &&
      requestedAmount > formatNumber(product.maxLeft) &&
      requestedAmount <= formatNumber(product.maxWithTolerance)
    ) {
      setErrorMessage({
        title: `Your requested amount exceeds the total on your agreement but is within normal tolerances`,
        description: [
          "You can still request an order and we will contact you if we are unable to fulfil within the terms of the agreement.",
          <br key="br3" />,
          <br key="br4" />,
          `You have ${maxLeftFormatted} ${UNIT_MAP_PLURAL[product.unit as keyof typeof UNIT_MAP_PLURAL]?.toLowerCase()} remaining.`,
        ],
        color: "var(--info-100)",
      });
    } else if (
      !product ||
      requestedAmount > formatNumber(Math.max(product.quantity.remaining, 0))
    ) {
      setErrorMessage({
        title: `Your requested amount exceeds your remaining amount in this month.`,
        description: [
          "You can still request an order and we will contact you if we are unable to fulfil within the terms of the agreement.",
          <br key="br5" />,
          <br key="br6" />,
          `You have ${maxLeftFormatted} ${UNIT_MAP_PLURAL[product.unit as keyof typeof UNIT_MAP_PLURAL]?.toLowerCase()} remaining.`,
        ],
        color: "var(--info-100)",
      });
    } else {
      setErrorMessage(undefined);
    }
  };

  useEffect(() => {
    if (disabled) {
      validateAmount(amounts[product.productVariantNumber]?.requestQuantity || "");
    }
  }, [disabled]);

  return (
    <Stack
      key={product.productDescription}
      spacing={2}
      sx={{
        p: 2,
        border: `1px solid ${colors.grey200}`,
        backgroundColor: "#ffffff",
      }}
    >
      <Stack>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="h4" color="primary500">
            {product.productDescription}
          </Typography>
        </Stack>
        <Typography variant="body2" color="secondary700">
          {formatPrice(totalAmount)} total ({formatPrice(product.unitPrice)} per&nbsp;
          {UNIT_MAP_LONG[product.unit as keyof typeof UNIT_MAP_LONG]})
        </Typography>
      </Stack>

      {disabled && (
        <Stack direction="row" spacing={3}>
          <Stack>
            <Input
              sx={{ width: "200px" }}
              testId="test-input"
              label="Requested"
              disabled={disabled}
              value={amounts[product.productVariantNumber]?.requestQuantity || ""}
              type={Capacitor.isNativePlatform() ? "tel" : "number"}
              disableError
            />
          </Stack>
          {!hideRemainder && (
            <Stack spacing={1.5}>
              <Typography variant="body2" sx={{ color: "black", fontWeight: 700 }}>
                Remaining total
              </Typography>
              <Typography variant="h4" color="primary500">
                {maxLeftFormatted}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      {!disabled && (
        <Stack>
          <Stack direction="row" gap={4}>
            <Stack>
              <Typography variant="body2" sx={{ color: "black", fontWeight: 700 }}>
                Committed
              </Typography>
              <Typography variant="body2" color="grey700">
                {month}
              </Typography>
              <Typography variant="h4" color="primary500">
                {formatNumber(committedAmount)}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="body2" sx={{ color: "black", fontWeight: 700 }}>
                Remaining
              </Typography>
              <Typography variant="body2" color="grey700">
                {month}
              </Typography>
              <Typography variant="h4" color="primary500">
                {formatNumber(
                  committedAmount - orderedAmount > 0 ? committedAmount - orderedAmount : 0
                )}
              </Typography>
            </Stack>
            <Stack>
              <Typography variant="body2" sx={{ height: "100%", color: "black", fontWeight: 700 }}>
                Total Remaining
              </Typography>
              <Typography variant="h4" color="primary500">
                {maxLeftFormatted}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      )}

      <Stack spacing={2}>
        {!disabled && !toleranceMaxed && (
          <UnscrollableNumberInput
            amounts={amounts}
            disabled={disabled}
            handleAmountChange={handleAmountChange}
            validateAmount={validateAmount}
            product={product}
          />
        )}
        {toleranceMaxed && (
          <ErrorBox
            title="You have already ordered the total remaining of this product."
            description={[
              "Please contact your Farmlands representative if you would like to order more.",
            ]}
            color={colors.primary100}
          />
        )}

        {errorMessage && <ErrorBox {...errorMessage} />}

        {disabled && showAdditives && product.feedRate && (
          <FeedRateDisplay feedRate={product.feedRate} additives={product.additives} />
        )}
        {/* Integrate AdditivesOption */}
        {!disabled && showAdditives && (
          <AdditivesOption
            product={{
              ...product,
              feedRate: amounts[product.productVariantNumber]?.feedRate
                ? {
                    label:
                      feedRates.find(
                        (rate) => rate.value === amounts[product.productVariantNumber]?.feedRate
                      )?.label ?? "",
                    value: amounts[product.productVariantNumber]?.feedRate ?? "",
                  }
                : undefined,
              additives: amounts[product.productVariantNumber]?.additives ?? "",
              deliveryMonthPlans: [],
              itemNumber: product.productVariantNumber,
              productId: 0,
              description: product.productDescription,
              lineNumber: 0,
              quantity: 0,
              unit: "",
              unitPrice: 0,
            }}
            editable={true}
            setEnable={(enabled) => {
              handleAmountChange(product, {
                feedRate: enabled ? feedRates[0].value : undefined,
                additives: enabled ? "" : undefined,
              });
            }}
            setFeedRate={(value) => {
              handleAmountChange(product, { feedRate: value });
            }}
            setAdditives={(value) => {
              handleAmountChange(product, { additives: value });
            }}
          />
        )}
      </Stack>
    </Stack>
  );
};

interface ErrorBoxProps {
  title: string;
  description: React.ReactNode[];
  color: string;
}

export const ErrorBox: React.FC<ErrorBoxProps> = ({ title, description, color }) => {
  return (
    <Alert
      severity="warning"
      icon={<MaterialSymbol sx={{ color: "#222" }}>info</MaterialSymbol>}
      sx={{
        backgroundColor: color,
        "& .MuiAlert-message": {
          width: "100%",
        },
        mt: 0,
      }}
    >
      <Typography variant="body2" fontWeight={700}>
        {title}
      </Typography>
      <Typography variant="body2">{description.map((line) => line)}</Typography>
    </Alert>
  );
};

export default ProductLine;

interface UnscrollableNumberInputProps {
  disabled?: boolean;
  amounts: Record<string, ProductLineValues>;
  product: ProductRequest;
  handleAmountChange: (product: ProductRequest, updatedValues: Partial<ProductLineValues>) => void;
  validateAmount: (value: string) => void;
}

const UnscrollableNumberInput: React.FC<UnscrollableNumberInputProps> = ({
  validateAmount,
  handleAmountChange,
  product,
  disabled,
  amounts,
}) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!ref.current) {
      return;
    }
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
    };
    ref.current.addEventListener("wheel", handleWheel);

    return () => {
      if (!ref.current) {
        return;
      }
      ref.current.removeEventListener("wheel", handleWheel);
    };
  }, []);
  return (
    <Input
      ref={ref}
      testId="test-input"
      label="Amount"
      inputBaseProps={{
        inputMode: "decimal",
        pattern: "[0-9]*[.]?[0-9]*",
        min: 0,
        step: 0.01,
      }}
      disabled={disabled}
      value={amounts[product.productVariantNumber]?.requestQuantity || ""}
      onChange={(e) => {
        // Strip leading zeros from integers only (e.g. "007" → "7") but preserve
        // mid-typing decimals like "0." and "11." so the decimal point isn't erased.
        const requestQuantity = e.target.value.replace(/^0+(?=\d)/, "");
        handleAmountChange(product, { requestQuantity });
        validateAmount(requestQuantity);
      }}
      type={Capacitor.isNativePlatform() ? "tel" : "number"}
      disableError
    />
  );
};
